// BackToTopButton.tsx
import React, { useEffect, useState, useCallback, KeyboardEvent } from "react";
import theme from "../../style/theme";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";

/**
 * 「上に戻る」ボタン。
 * - スクロール量が閾値を超えたら表示
 * - クリック/Enter/Space で先頭へスクロール
 */
export type BackToTopButtonProps = {
  threshold?: number;      // 何pxスクロールしたら表示するか
  right?: number;          // 右からの距離(px)
  bottom?: number;         // 下からの距離(px)
  label?: string;          // アクセシビリティ用ラベル
  size?: number;           // ボタンの幅/高さ(px)
  smooth?: boolean;        // スムーススクロールの利用可否（既定: true）
  zIndex?: number;         // 重なり順序
  // カラー/影
  bgColor?: string;
  fgColor?: string;
  shadow?: string;
  hoverShadow?: string;
  focusOutline?: string;
};

const isBrowser = typeof window !== "undefined";

export const BackToTopButton: React.FC<BackToTopButtonProps> = ({
  threshold = 300,
  right = 24,
  bottom = 24,
  label = "ページ上部へ移動",
  size = 44,
  smooth = true,
  zIndex = 1000,
  bgColor = theme.palette.primary.main,
  fgColor = "#ffffff",
  shadow = "0 6px 16px rgba(0,0,0,0.2)",
  hoverShadow = "0 8px 20px rgba(0,0,0,0.25)",
  focusOutline = "#cfe0ff",

}) => {
  const [visible, setVisible] = useState(false);

  // 表示/非表示の監視
  useEffect(() => {
    if (!isBrowser) return;

    const onScroll = () => {
      // 閾値を超えたら表示
      setVisible(window.scrollY > threshold);
    };

    onScroll(); // 初期判定
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [threshold]);

  // 先頭へスクロール
  const scrollToTop = useCallback(() => {
    if (!isBrowser) return;

    const reduced =
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

    window.scrollTo({
      top: 0,
      behavior: smooth && !reduced ? "smooth" : "auto",
    });
  }, [smooth]);

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      scrollToTop();
    }
  };

  if (!visible) return null;

  //表示位置の設定
  const containerStyle: React.CSSProperties = {
    position: "fixed", // スクロールしても表示位置を変えない
    right,
    bottom,
    zIndex,
  };

  const btnStyle: React.CSSProperties = {
    width: size,
    height: size,
    borderRadius: "50%", // 丸
    background: bgColor,
    color: fgColor,
    border: `2px solid ${theme.palette.primary.main}`,
    boxShadow: shadow,
    cursor: "pointer",
    display: "grid",
    placeItems: "center",
    fontSize: Math.round(size * 0.45),
    lineHeight: 1,
    transition: "box-shadow 0.15s ease",
  };

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={label}
      onClick={scrollToTop}
      onKeyDown={onKeyDown}
      style={containerStyle}
      // フォーカス可視化
      onFocus={(e) => {
        (e.currentTarget.firstChild as HTMLElement)?.style.setProperty(
          "outline",
          `2px solid ${focusOutline}`
        );
      }}
      onBlur={(e) => {
        (e.currentTarget.firstChild as HTMLElement)?.style.removeProperty(
          "outline"
        );
      }}

      onMouseEnter={(e) => {
        (e.currentTarget.firstChild as HTMLElement)?.style.setProperty(
          "box-shadow",
          hoverShadow
        );
      }}
      onMouseLeave={(e) => {
        (e.currentTarget.firstChild as HTMLElement)?.style.setProperty(
          "box-shadow",
          shadow
        );
      }}
    >
      <button type="button" aria-hidden="true" style={btnStyle} title="ページ上部へ移動">
        <ArrowUpwardRoundedIcon />
      </button>
    </div>
  );
};

export default BackToTopButton;
